import * as THREE from 'three';
import Application from '../Application';
import MonitorScreen from './MonitorScreen';
import CoffeeSteam from './CoffeeSteam';
import AudioManager from '../Audio/AudioManager';
import { DESK_OBJECTS, BACKGROUND_CONFIG, LIGHTING_CONFIG, FLOOR_CONFIG, SCENE_BACKGROUND, GRID_BACKGROUND_CONFIG, ORGANIC_BACKGROUND_CONFIG, } from './deskSceneConfig';
import { placeModelInScene } from './placeModelInScene';
import GridBackground from './GridBackground';
import OrganicBackground from './OrganicBackground';
export default class World {
    constructor() {
        this.gridBackground = null;
        this.organicBackground = null;
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        this.resources.on('ready', () => {
            // Scene background - so surroundings aren't pure black
            this.scene.background = new THREE.Color(SCENE_BACKGROUND);
            // Simulation-style grid background (optional)
            if (GRID_BACKGROUND_CONFIG.enabled) {
                this.gridBackground = new GridBackground(this.application, GRID_BACKGROUND_CONFIG);
            }
            // Organic / lava-lamp style: drifting blobs, mouse disturbs (keeps 3D feel)
            if (ORGANIC_BACKGROUND_CONFIG.enabled) {
                this.organicBackground = new OrganicBackground(this.application, ORGANIC_BACKGROUND_CONFIG);
            }
            // Lights - GLB materials need lighting or the scene stays black
            const { ambient, directional, hemisphere } = LIGHTING_CONFIG;
            const ambientLight = new THREE.AmbientLight(ambient.color, ambient.intensity);
            this.scene.add(ambientLight);
            const dirLight = new THREE.DirectionalLight(directional.color, directional.intensity);
            dirLight.position.set(directional.position[0], directional.position[1], directional.position[2]);
            if (directional.castShadow) {
                dirLight.castShadow = true;
                dirLight.shadow.mapSize.width = 2048;
                dirLight.shadow.mapSize.height = 2048;
                dirLight.shadow.camera.near = 0.5;
                dirLight.shadow.camera.far = 2500;
                dirLight.shadow.camera.left = -800;
                dirLight.shadow.camera.right = 800;
                dirLight.shadow.camera.top = 800;
                dirLight.shadow.camera.bottom = -800;
                dirLight.shadow.bias = -0.0001;
            }
            this.scene.add(dirLight);
            if (hemisphere) {
                const hemi = new THREE.HemisphereLight(hemisphere.sky, hemisphere.ground, hemisphere.intensity);
                hemi.position.set(hemisphere.position[0], hemisphere.position[1], hemisphere.position[2]);
                this.scene.add(hemi);
            }
            // Floor - simple plane so the desk has ground
            if (FLOOR_CONFIG.enabled) {
                const floorGeo = new THREE.PlaneGeometry(FLOOR_CONFIG.size[0], FLOOR_CONFIG.size[1]);
                const floorMat = new THREE.MeshStandardMaterial({
                    color: FLOOR_CONFIG.color,
                });
                const floor = new THREE.Mesh(floorGeo, floorMat);
                const [rx, ry, rz] = FLOOR_CONFIG.rotation;
                floor.rotation.set(rx * (Math.PI / 180), ry * (Math.PI / 180), rz * (Math.PI / 180));
                floor.position.set(FLOOR_CONFIG.position[0], FLOOR_CONFIG.position[1], FLOOR_CONFIG.position[2]);
                floor.receiveShadow = true;
                this.scene.add(floor);
            }
            // Optional background (room etc.) - load first
            if (BACKGROUND_CONFIG &&
                BACKGROUND_CONFIG.path !== '' &&
                this.resources.items.gltfModel['background']) {
                placeModelInScene(this.scene, this.resources.items.gltfModel['background'], BACKGROUND_CONFIG);
            }
            // Desk objects from config (only place when enabled !== false)
            for (const obj of DESK_OBJECTS) {
                if (obj.enabled === false)
                    continue;
                const gltf = this.resources.items.gltfModel[obj.id];
                if (gltf)
                    placeModelInScene(this.scene, gltf, obj);
            }
            // Enable shadows on all meshes (desk, PC, etc.) so keyboard/objects have visible shadow on table
            this.scene.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            });
            this.monitorScreen = new MonitorScreen();
            this.coffeeSteam = new CoffeeSteam();
            this.audioManager = new AudioManager();
        });
    }
    update() {
        if (this.gridBackground)
            this.gridBackground.update();
        if (this.organicBackground)
            this.organicBackground.update();
        if (this.audioManager)
            this.audioManager.update();
        if (this.monitorScreen)
            this.monitorScreen.update();
        if (this.coffeeSteam)
            this.coffeeSteam.update();
    }
}
